import { AppointmentStatuses } from '~constants/maps';

import dayjs, { dateInputFieldFormat, timeValueFormat } from '~libs/dayjs';

import { FormDefault } from '~main/reducers/formDefaults';

interface AppointmentDefaults {
  date?: string;
  start_time?: string;
  end_time?: string;
  status_id?: number;
  notes?: string;
  [key: string]: string | number | undefined;
}

interface CompanySettings {
  note_header?: string;
}

interface DefaultDependencies {
  companySettings?: CompanySettings;
}

type FormType = 'case-management' | 'support-coordination' | 'single' | 'group';

const getBaseQuickAppointmentDefaults = (): AppointmentDefaults => ({
  date: dayjs().format(dateInputFieldFormat),
  start_time: dayjs().subtract(1, 'hour').startOf('hour').format(timeValueFormat),
  end_time: dayjs().startOf('hour').format(timeValueFormat),
  status_id: AppointmentStatuses.COMPLETED,
});

const extractFormDefaults = (
  companyFormDefaults: FormDefault[],
  featureType: string,
): Record<string, string | undefined> => {
  return Object.fromEntries(
    companyFormDefaults
      .filter(({ feature_type }) => feature_type === featureType)
      .map(({ key, value }) => [key, value]),
  );
};

const getCaseManagementDefaults = (
  dependencies: DefaultDependencies,
  companyFormDefaults: FormDefault[],
): AppointmentDefaults => {
  const noteHeader = dependencies.companySettings?.note_header;
  const defaultOverrides = extractFormDefaults(companyFormDefaults, 'casemanagement');

  return {
    ...getBaseQuickAppointmentDefaults(),
    ...(noteHeader && { notes: noteHeader }),
    ...defaultOverrides,
  };
};

const getDefaults = (
  form: FormType,
  dependencies: DefaultDependencies = {},
  companyFormDefaults: FormDefault[] = [],
): AppointmentDefaults => {
  switch (form) {
    case 'support-coordination':
      return getBaseQuickAppointmentDefaults();
    case 'case-management':
      return getCaseManagementDefaults(dependencies, companyFormDefaults);
    case 'single':
    case 'group':
    default:
      return {};
  }
};

export { getDefaults };
